import React from "react";
import { Box, Button, Typography, IconButton, Stack } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

interface FileUploadProps {
  onFileSelect: (files: File[] | undefined) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  selectedFiles?: File[];
  maxFileSize?: number;
  acceptedFileTypes?: string[];
  multiple?: boolean;
  maxFiles?: number;
  showRestrictions?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  label,
  required = false,
  disabled = false,
  selectedFiles = [],
  maxFileSize = 5 * 1024 * 1024, // 5MB default
  acceptedFileTypes = [".pdf", ".jpg", ".jpeg", ".png"],
  multiple = false,
  maxFiles = 5,
  showRestrictions = true,
}) => {
  const { t } = useTranslation();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleClick = () => {
    inputRef.current?.click();
  };

  const validateFile = (file: File): string | undefined => {
    if (file.size > maxFileSize) {
      return t("error.fileTooLarge", { maxSize: "5MB" });
    }
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
    if (!acceptedFileTypes.includes(fileExtension)) {
      return t("error.invalidFileType", {
        types: acceptedFileTypes.join(", "),
      });
    }
    return undefined;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList) return;

    const newFiles = Array.from(fileList);
    const validFiles: File[] = [];
    const errors: string[] = [];

    newFiles.forEach((file) => {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else if (multiple) {
        if (selectedFiles.length + validFiles.length < maxFiles) {
          validFiles.push(file);
        } else {
          errors.push(t("error.tooManyFiles", { max: maxFiles }));
        }
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      alert(errors.join("\n"));
    }

    if (validFiles.length > 0) {
      if (multiple) {
        onFileSelect([...selectedFiles, ...validFiles]);
      } else {
        onFileSelect([validFiles[0]]);
      }
    }
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    onFileSelect(newFiles.length > 0 ? newFiles : undefined);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  return (
    <Box>
      <input
        type="file"
        ref={inputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
        required={required}
        disabled={disabled}
        multiple={multiple}
        accept={acceptedFileTypes.join(",")}
      />
      <Stack spacing={1}>
        {selectedFiles.length > 0 ? (
          <>
            {selectedFiles.map((file, index) => (
              <Box
                key={`${file.name}-${index}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 2,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.paper",
                }}
              >
                <AttachFileIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {file.name} ({(file.size / (1024 * 1024)).toFixed(1)}MB)
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveFile(index)}
                  disabled={disabled}
                  sx={{ ml: 1 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            {multiple && selectedFiles.length < maxFiles && (
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                onClick={handleClick}
                disabled={disabled}
              >
                {t("common.addMoreFiles")}
              </Button>
            )}
          </>
        ) : (
          <Button
            variant="outlined"
            component="span"
            startIcon={<CloudUploadIcon />}
            onClick={handleClick}
            fullWidth
            disabled={disabled}
          >
            {label || t("common.upload")}
          </Button>
        )}
        {showRestrictions && (
          <Typography variant="caption" color="text.secondary">
            {t("common.fileRestrictions", {
              types: acceptedFileTypes.join(", "),
              maxSize: "5MB",
              maxFiles: multiple ? maxFiles : 1,
            })}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default FileUpload;
